import React, { useState } from "react";
import DOMPurify from "dompurify";
import {
    AlertTriangle,
    AtSign,
    CalendarDays,
    ChevronDown,
    ClipboardList,
    Clock,
    FileText,
    Mail,
    Pencil,
    Send,
} from "lucide-react";
import { isScored, scoreSummary, QUESTION_TYPE_LABEL } from "../../../screening/QuestionsBuilder";
import { stripHtml, hasFormLink, FORM_LINK_TOKEN, VARIABLES, FORM_LINK_VARIABLE, isCallStep, isWhatsAppStep, isEmailStep } from "../sequenceBuilderApi";
import "../gradientAccents.css";

const startLabel = (t) => (t === "next_business_day" ? "Next business day" : "Immediately");

// Render {{tokens}} as their human label, matching the candidate-eye preview in EmailTestModal
// so the review and the test email agree on what the candidate will actually see.
const VARIABLE_LABELS = Object.fromEntries(
    [...VARIABLES, FORM_LINK_VARIABLE].map((v) => [v.token, v.label])
);
const applyVariablePreview = (html) => {
    if (!html) return html;
    return html.replace(/\{\{([^}]+)\}\}/g, (match, inner) => {
        const label =
            VARIABLE_LABELS[match] ?? inner.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        return `<em style="color:#7c3aed">${label}</em>`;
    });
};

// Section caption + hairline rule, as used by the job-role review step.
const SectionLabel = ({ children, onEdit, editTitle }) => (
    <div className="flex gap-2 items-center mb-4">
        <label className="text-sm dark:text-gray-400 text-textSecondary whitespace-nowrap">{children}</label>
        <div className="flex-grow bg-gray-300 dark:bg-gray-700" style={{ height: "0.5px" }} />
        {onEdit && (
            <button
                type="button"
                onClick={onEdit}
                className="text-gray-400 dark:text-gray-500 hover:text-[#2960FD] shrink-0"
                title={editTitle}
            >
                <Pencil className="w-4 h-4" />
            </button>
        )}
    </div>
);

// Hidden gradient the stat icons stroke themselves with — same approach as the job-role
// review step, which paints its icons with `stroke: url(#grad1)` off an off-screen <defs>.
const GRADIENT_ID = "seqReviewIconGrad";
const gradientDef = (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
            <linearGradient id={GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
        </defs>
    </svg>
);

const Stat = ({ icon: Icon, label, value, muted }) => (
    <div className="flex items-center gap-2 min-w-0">
        <Icon
            className="w-5 h-5 shrink-0"
            style={{ stroke: `url(#${GRADIENT_ID})`, fill: "none" }}
            fill="none"
            strokeWidth={2}
        />
        <div className="min-w-0">
            <p className="text-[11px] dark:text-gray-400 text-textSecondary leading-tight">{label}</p>
            <p
                className={`text-sm font-medium truncate ${muted ? "dark:text-gray-500 text-gray-400 italic" : "dark:text-white text-textPrimary"}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value}
            </p>
        </div>
    </div>
);

/**
 * Wizard final step — a genuine pre-launch review rather than a summary strip.
 *
 * Three things it has to answer: what did I build, what will the candidate actually receive,
 * and is anything going to stop this from sending. So it leads with readiness checks, then a
 * details card that is meaningful even for a plain (no-screening) sequence, then the full
 * screening set, then every email with its real rendered body.
 */
const StepReview = ({ form, gmailEmail, onTestStep, onGoToStep }) => {
    const { name, includeScreening, questions, steps, startTiming } = form;
    const totalDays = steps[steps.length - 1].delayDays + 1;

    const [showAllQuestions, setShowAllQuestions] = useState(false);
    // First email open by default — enough to show the review has real content behind it
    // without turning a five-step sequence into a wall of text.
    const [openSteps, setOpenSteps] = useState(() => new Set([0]));
    const toggleStep = (idx) =>
        setOpenSteps((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });

    // ── Readiness checks ────────────────────────────────────────────────────────
    const issues = [];
    // Only an issue if something here actually sends mail.
    if (!gmailEmail && steps.some(isEmailStep)) {
        issues.push({
            text: "No sender mailbox is connected — these emails can't send until one is.",
            fix: () => onGoToStep("sequence"),
            fixLabel: "Connect",
        });
    }
    // Only an email step can carry the link — a call/WhatsApp-only outreach reaches the candidate
    // without one (the agent asks aloud; a WhatsApp step sends a fixed approved template).
    if (includeScreening && steps.some(isEmailStep) && !hasFormLink(steps)) {
        issues.push({
            text: `Screening is on, but no step contains ${FORM_LINK_TOKEN} — candidates won't be able to reach the form.`,
            fix: () => onGoToStep("sequence"),
            fixLabel: "Add it",
        });
    }
    // The mirror case, reachable after turning screening off on an outreach that had it: the
    // token has nothing to resolve to and sends as an empty gap mid-sentence.
    if (!includeScreening && hasFormLink(steps)) {
        issues.push({
            text: `Screening is off, but a step still contains ${FORM_LINK_TOKEN} — it would send as a blank gap.`,
            fix: () => onGoToStep("sequence"),
            fixLabel: "Remove it",
        });
    }
    steps.forEach((s, i) => {
        // Neither a call nor a WhatsApp step has authored subject/body to be missing.
        if (!isEmailStep(s)) return;
        if (!s.subject?.trim()) {
            issues.push({ text: `Email ${i + 1} has no subject line.`, fix: () => onGoToStep("sequence"), fixLabel: "Fix" });
        }
        if (!stripHtml(s.body)) {
            issues.push({ text: `Email ${i + 1} has no message body.`, fix: () => onGoToStep("sequence"), fixLabel: "Fix" });
        }
    });

    const shownQuestions = showAllQuestions ? questions : questions.slice(0, 4);

    return (
        <div className="w-full flex flex-col gap-6 pb-4">
            {gradientDef}
            <h1 className="text-lg font-light text-left dark:text-white text-textPrimary">
                Review your outreach before you launch it.
            </h1>

            {/* ── Readiness ──────────────────────────────────────────────────────── */}
            {/* Only surfaces when something needs attention — a clean sequence says nothing. */}
            {issues.length > 0 && (
                <div className="rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                            {issues.length} thing{issues.length > 1 ? "s" : ""} to check before launching
                        </p>
                    </div>
                    <ul className="flex flex-col gap-1.5 pl-6">
                        {issues.map((issue, i) => (
                            <li key={i} className="flex items-start gap-2 text-[13px] text-amber-800 dark:text-amber-200/90">
                                <span className="leading-relaxed">{issue.text}</span>
                                <button
                                    type="button"
                                    onClick={issue.fix}
                                    className="underline underline-offset-2 hover:no-underline shrink-0"
                                >
                                    {issue.fixLabel}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ── Details ────────────────────────────────────────────────────────── */}
            <div className="relative">
                <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1 z-10">
                    Outreach Details
                </label>
                <div className="w-full dark:glass-effect bg-bgPrimary p-4 pt-5 rounded-2xl border border-gray-600/50 mt-1">
                    <div className="flex items-start justify-between gap-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 flex-1 min-w-0">
                            <Stat icon={FileText} label="Outreach" value={name || "Untitled outreach"} />
                            <Stat
                                icon={ClipboardList}
                                label="Type"
                                value={includeScreening ? "Screening" : "Plain outreach"}
                            />
                            {/* Counts every step, so the label only says "Emails" when they all are. */}
                            <Stat
                                icon={Mail}
                                label={steps.every(isEmailStep) ? "Emails" : "Steps"}
                                value={`${steps.length} step${steps.length > 1 ? "s" : ""}`}
                            />
                            <Stat icon={CalendarDays} label="Runs over" value={`${totalDays} day${totalDays > 1 ? "s" : ""}`} />
                            <Stat icon={Clock} label="Starts" value={startLabel(startTiming)} />
                            {/* Nothing sends from a mailbox unless an email step exists. */}
                            {steps.some(isEmailStep) && (
                                <Stat
                                    icon={AtSign}
                                    label="Sends from"
                                    value={gmailEmail || "Not connected"}
                                    muted={!gmailEmail}
                                />
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => onGoToStep("create")}
                            className="text-gray-400 dark:text-gray-500 hover:text-[#2960FD] shrink-0"
                            title="Edit outreach details"
                        >
                            <Pencil className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Screening questions ────────────────────────────────────────────── */}
            {includeScreening && (
                <div className="relative">
                    <SectionLabel onEdit={() => onGoToStep("questions")} editTitle="Edit screening questions">
                        Screening questions ({questions.length})
                    </SectionLabel>
                    <div className="dark:glass-effect bg-bgPrimary w-full p-4 rounded-xl border border-gray-600/50">
                        <div className="flex flex-col gap-2.5">
                            {shownQuestions.map((q, i) => {
                                const summary = scoreSummary(q);
                                return (
                                    // Number and label share one text flow so their baselines
                                    // always match — as separate flex children the number sat
                                    // top-aligned while the label centred against the chips.
                                    <div key={q.key || i} className="flex items-center gap-2 flex-wrap text-sm">
                                        <span className="dark:text-gray-300 text-textPrimary">
                                            <span className="dark:text-gray-400 text-textSecondary">{i + 1}.</span>{" "}
                                            {q.label || (
                                                <span className="dark:text-gray-400 text-textSecondary italic">
                                                    Untitled question
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 dark:bg-white/10 dark:text-gray-400 text-textSecondary whitespace-nowrap">
                                            {QUESTION_TYPE_LABEL[q.type]}
                                        </span>
                                        {q.required && (
                                            <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 dark:bg-white/10 dark:text-gray-400 text-textSecondary whitespace-nowrap">
                                                Required
                                            </span>
                                        )}
                                        {isScored(q) && (
                                            <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 dark:bg-white/10 dark:text-gray-400 text-textSecondary whitespace-nowrap">
                                                Knock-out{summary ? `: ${summary}` : ""}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {questions.length > 4 && (
                            <button
                                type="button"
                                onClick={() => setShowAllQuestions((v) => !v)}
                                className="mt-3 text-sm dark:text-gray-300 text-textSecondary hover:opacity-80 underline underline-offset-2"
                            >
                                {showAllQuestions ? "View Less" : `View All (${questions.length})`}
                            </button>
                        )}

                        {questions.some(isScored) && (
                            <p className="text-[11px] dark:text-gray-400 text-textSecondary mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/50">
                                Knock-out = the candidate is automatically marked not qualified when their answer
                                doesn&apos;t match.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* ── The emails ─────────────────────────────────────────────────────── */}
            <div className="relative">
                <SectionLabel onEdit={() => onGoToStep("sequence")} editTitle="Edit the outreach">
                    The candidate receives ({steps.length})
                </SectionLabel>

                <div className="flex flex-col gap-3">
                    {steps.map((step, idx) => {
                        const open = openSteps.has(idx);
                        const dayLabel = idx === 0 ? startLabel(startTiming) : `Day ${step.delayDays + 1}`;
                        return (
                            <div
                                key={idx}
                                className="dark:glass-effect bg-bgPrimary w-full rounded-xl border border-gray-600/50 overflow-hidden"
                            >
                                <div className="flex items-center gap-3 px-4 py-3">
                                    <span className="w-7 h-7 rounded-full seq-gradient-fill text-white flex items-center justify-center text-[13px] font-medium shrink-0">
                                        {idx + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[13px] dark:text-gray-400 text-textSecondary">{dayLabel}</p>
                                        <p className="text-sm font-medium dark:text-white text-textPrimary truncate">
                                            {isCallStep(step) ? (
                                                "AI Call — asks your screening questions"
                                            ) : isWhatsAppStep(step) ? (
                                                `WhatsApp — ${step.whatsappTemplateName || "approved template"}`
                                            ) : (
                                                step.subject?.trim() || (
                                                    <span className="dark:text-gray-500 text-gray-400 italic font-normal">
                                                        No subject yet
                                                    </span>
                                                )
                                            )}
                                        </p>
                                    </div>
                                    {step.sendAt && idx > 0 && (
                                        <span className="text-[13px] dark:text-gray-400 text-textSecondary shrink-0 hidden sm:inline">
                                            {step.sendAt}
                                        </span>
                                    )}
                                    {/* Neither applies to a call step: there is no email to test and
                                        no body to expand. */}
                                    {isEmailStep(step) && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => onTestStep(step)}
                                                className="flex items-center gap-1.5 text-[13px] dark:text-white text-textSecondary border border-gray-300 dark:border-gray-600 rounded-full px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-white/10 transition-all shrink-0"
                                                title="Preview and send a test"
                                            >
                                                <Send className="w-3.5 h-3.5" /> Test
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleStep(idx)}
                                                className="text-gray-400 dark:text-gray-500 hover:text-[#2960FD] shrink-0"
                                                title={open ? "Hide email" : "View email"}
                                            >
                                                <ChevronDown className={`w-5 h-5 transition-transform ${open ? "" : "-rotate-90"}`} />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Only an email step has a sender and an authored body to show. A
                                    call/WhatsApp step's card says what it does in the header, so
                                    there is nothing to expand. */}
                                {open && isEmailStep(step) && (
                                    <div className="border-t border-gray-200 dark:border-gray-700/50 px-4 py-4">
                                        <div className="flex items-center gap-3 pb-3 mb-3 border-b border-dashed border-gray-200 dark:border-gray-700/50">
                                            <span className="text-[13px] dark:text-gray-400 text-textSecondary w-16 shrink-0">
                                                From
                                            </span>
                                            <span className="text-[13px] dark:text-gray-300 text-textSecondary truncate">
                                                {gmailEmail || (
                                                    <span className="italic dark:text-gray-500 text-gray-400">
                                                        No mailbox connected
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {stripHtml(step.body) ? (
                                            <div
                                                className="text-sm dark:text-gray-300 text-textSecondary leading-relaxed max-h-[320px] overflow-y-auto"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(applyVariablePreview(step.body) || "") }}
                                            />
                                        ) : (
                                            <p className="text-sm italic dark:text-gray-500 text-gray-400">
                                                This email has no message body yet.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default StepReview;
